import { Fragment, useCallback, useState } from 'react';
import { useCollection } from '@/firebase/hooks/useCollection';
import classNames from '@/helpers/classNames';
import { useSelector } from '@/store';
import { userSelector } from '@/store/selectors/auth';
import { Menu, Transition } from '@headlessui/react';
import { BellIcon } from '@heroicons/react/24/outline';
import { doc, getDoc, updateDoc, where } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import { LOTTIE } from '@/assets';
import firestore from '@/firebase/firestore';
import { Notification } from '@/types/Notifications';
import { PurchaseOrder } from '@/types/PurchaseOrder';
import PoAssignModal from '@/screens/PO/PoAssignModal';
import { List, ListRowProps } from 'react-virtualized';

function Notifications() {
  const user = useSelector(userSelector);
  const navigate = useNavigate();
  const [assignModalPo, setAssignModalPo] = useState<PurchaseOrder | null>(null);

  // eslint-disable-next-line prefer-const
  const { data, loading, refetch } = useCollection(
    'notifications',
    false,
    where('userId', '==', user?.uid),
    where('clicked', '==', false)
  );

  let notifications = data;

  notifications = notifications?.sort((a, b) => b.date.toMillis() - a.date.toMillis());

  const unreadNotifications = notifications?.filter((notification) => !notification.read);

  const getHref = useCallback((n: Notification) => {
    if (n.type === 'order') {
      // TODO: Assign QC modal
      return null;
    } else return `/po/${n.refId}`;
  }, []);

  const setNotificationsAsRead = () => {
    if (unreadNotifications?.length > 0) {
      unreadNotifications.forEach(async (notification) => {
        await updateDoc(doc(firestore, 'notifications', notification.id), {
          read: true
        });
      });
    }
  };

  const renderRow = useCallback(
    ({ index, key, style }: ListRowProps) => {
      const notification = notifications[index];
      return (
        <div style={style}>
          <Menu.Item key={key}>
            {({ active }) => (
              <Link
                to={getHref(notification) || '#'}
                onClick={async (ev) => {
                  const href = getHref(notification);
                  if (href === null) {
                    ev.preventDefault();
                    if (notification.type === 'order') {
                      const po = await getDoc(doc(firestore, 'orders', notification.refId));
                      setAssignModalPo({ ...po.data(), id: po.id } as PurchaseOrder);
                    } else if (notification.type === 'order_inspected') {
                      navigate(`/po/${notification.refId}/report`);
                    } else if (notification.type === 'order_assigned') {
                      navigate(`/po/${notification.refId}/inspection`);
                    }
                  }
                  updateDoc(doc(firestore, 'notifications', notification.id), {
                    clicked: true
                  });
                }}
                className={classNames(
                  active ? 'bg-gray-100' : '',
                  'relative block h-14 max-h-14 px-4 py-2 text-sm text-gray-700'
                )}>
                <h6 className="relative flex justify-between">
                  {notification.title}
                  <span className="pr-2 text-xs text-gray-400">
                    {notification.date.toDate().toLocaleString()}
                  </span>
                  {/* unread dot */}
                  {!notification.read && (
                    <span className="absolute top-2 right-0 inline-flex translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full bg-red-600 px-1 py-1 text-xs font-bold leading-none text-white" />
                  )}
                </h6>
                {notification.message}
              </Link>
            )}
          </Menu.Item>
        </div>
      );
    },
    [getHref, navigate, notifications]
  );

  //   Notification dropdown
  return (
    <>
      <Menu as="div" className="relative ml-3">
        <div>
          <Menu.Button
            className={classNames(
              'flex items-center rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary',
              loading ? 'animate-bounce ring-blue-600' : ''
            )}
            data-testid="notification-menu">
            <BellIcon className="h-6 w-6" aria-hidden="true" />
            {unreadNotifications?.length > 0 && (
              <span className="absolute top-0 right-0 inline-flex translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full bg-primary px-2 py-1 text-xs font-bold leading-none text-white">
                {unreadNotifications.length}
              </span>
            )}
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transitionyar ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
          afterLeave={setNotificationsAsRead}>
          <Menu.Items
            static
            className="absolute -right-3 z-50 mt-2 max-h-96 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            {notifications?.length > 0 ? (
              <List
                rowCount={notifications.length}
                rowHeight={56}
                width={550}
                height={384}
                rowRenderer={renderRow}
              />
            ) : (
              <div className="px-4 py-2 text-center text-sm text-gray-700">
                <Lottie
                  animationData={LOTTIE.notFound3}
                  style={{
                    height: 120,
                    marginBottom: 20
                  }}
                />
                No notifications
              </div>
            )}
          </Menu.Items>
        </Transition>
      </Menu>
      {assignModalPo && (
        <PoAssignModal
          po={assignModalPo}
          onAssigned={async () => {
            refetch();
          }}
          onClose={() => {
            setAssignModalPo(null);
          }}
        />
      )}
    </>
  );
}

export default Notifications;
